/**
 * Settings Icons
 * 
 * Icons used in user settings, preferences, and account management.
 * These appear in settings pages and user profile menus.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { getIconClasses } from '../iconUtils';

const iconPropTypes = {
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl']),
  color: PropTypes.oneOf(['inherit', 'primary', 'secondary', 'success', 'warning', 'danger', 'neutral', 'white']),
  className: PropTypes.string,
};

/**
 * AccountIcon - User profile icon
 */
export const AccountIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

AccountIcon.propTypes = iconPropTypes;

/**
 * SubscriptionIcon - Credit card icon for subscription/billing
 */
export const SubscriptionIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

SubscriptionIcon.propTypes = iconPropTypes;

/**
 * PreferencesIcon - Gear/cog icon for preferences/settings
 */
export const PreferencesIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

PreferencesIcon.propTypes = iconPropTypes;

/**
 * SecurityIcon - Shield with checkmark for security settings
 */
export const SecurityIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

SecurityIcon.propTypes = iconPropTypes;

/**
 * NotificationsIcon - Bell icon for notifications
 */
export const NotificationsIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

NotificationsIcon.propTypes = iconPropTypes;

/**
 * LogoutIcon - Exit/logout icon
 */
export const LogoutIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </svg>
);

LogoutIcon.propTypes = iconPropTypes;

/**
 * FeedbackIcon - Speech bubble icon for feedback
 */
export const FeedbackIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

FeedbackIcon.propTypes = iconPropTypes;

/**
 * ShareIcon - Share icon for sharing content
 */
export const ShareIcon = ({ size = 'md', color = 'inherit', className = '', ...props }) => (
  <svg 
    className={getIconClasses(size, color, className)} 
    fill="none" 
    stroke="currentColor" 
    viewBox="0 0 24 24"
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
  </svg>
);

ShareIcon.propTypes = iconPropTypes;
